import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { LogOut, Download, Loader2, Sparkles } from 'lucide-react';
import { ProfileUsage } from './ProfileUsage';
import { XBookmarksSettings } from '@/components/settings/XBookmarksSettings';
import { exportCanvasToJPG } from '@/lib/canvasExport';
import { useCanvasStore } from '@/stores/canvasStore';
import { usePartnrBetaStore } from '@/stores/partnrBetaStore';
import { toast } from 'sonner';

export function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);
  const [isXSettingsOpen, setIsXSettingsOpen] = useState(false);
  const { viewMode, items, currentBoardId } = useCanvasStore();
  const { isPartnrBetaEnabled, setPartnrBetaEnabled } = usePartnrBetaStore();
  
  // Only show export option when in focus mode with items on the current board
  const boardItems = items.filter(i => i.board_id === currentBoardId);
  const canExport = viewMode === 'focus' && boardItems.length > 0;

  if (!user) return null;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportCanvasToJPG();
      toast.success('Canvas exported!');
    } catch (err) {
      toast.error('Export failed: ' + (err as Error).message);
    } finally {
      setIsExporting(false);
    }
  };

  const userInitials = user.user_metadata?.full_name
    ? user.user_metadata.full_name.split(' ').map((n: string) => n[0]).join('').toUpperCase()
    : user.email?.charAt(0).toUpperCase() || 'U';

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger className="focus:outline-none">
        <Avatar className="h-8 w-8 cursor-pointer ring-2 ring-transparent hover:ring-primary/20 transition-all">
          <AvatarImage 
            src={user.user_metadata?.avatar_url || user.user_metadata?.picture} 
            alt={user.user_metadata?.full_name || 'User'} 
          />
          <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
            {userInitials}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        side="right" 
        align="start" 
        sideOffset={16}
        className="w-72 p-0 rounded-2xl shadow-xl border-border/50 overflow-hidden bg-background z-50"
      >
        {/* User Info Header */}
        <div className="px-5 py-4 bg-muted/30 border-b border-border/50">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage 
                src={user.user_metadata?.avatar_url || user.user_metadata?.picture} 
                alt={user.user_metadata?.full_name || 'User'} 
              />
              <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {user.user_metadata?.full_name || 'User'}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {user.email}
              </p>
            </div>
          </div>
        </div>

        {/* Usage Section */}
        <ProfileUsage />

        {/* Partnr 1.1 Beta Toggle */}
        <div className="px-4 py-3 border-t border-border/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-partnr" />
              <span className="text-sm font-medium">Partnr 1.1 Beta</span>
            </div>
            <Switch
              checked={isPartnrBetaEnabled}
              onCheckedChange={setPartnrBetaEnabled}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Enables AI clustering & board profiles
          </p>
        </div>

        {/* X Bookmarks */}
        <div className="p-2 border-t border-border/50">
          <DropdownMenuItem
            onClick={() => setIsXSettingsOpen(true)}
            className="cursor-pointer rounded-xl h-10"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            X Bookmarks
          </DropdownMenuItem>
        </div>

        {/* Export Option - only in focus mode with items */}
        {canExport && (
          <div className="p-2 border-t border-border/50">
            <DropdownMenuItem 
              onClick={handleExport}
              disabled={isExporting}
              className="cursor-pointer rounded-xl h-10"
            >
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Export as JPG
            </DropdownMenuItem>
          </div>
        )}

        {/* Sign Out */}
        <div className="p-2 border-t border-border/50">
          <DropdownMenuItem 
            onClick={handleSignOut} 
            className="text-destructive focus:bg-destructive focus:text-white cursor-pointer rounded-xl h-10"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>

    <XBookmarksSettings open={isXSettingsOpen} onOpenChange={setIsXSettingsOpen} />
    </>
  );
}
